import { createFileRoute, Link } from "@tanstack/react-router";
import type { CorpusResponse, ResponseMode } from "@/api/types";
import corpusResponses from "@/fixtures/corpus-responses.json";
import { useResponseSurface } from "@/api/hooks";
import { AgentLoadingState } from "@/components/agent/AgentLoadingState";
import { ResponseSurface } from "@/components/response/ResponseSurface";

const RESPONSES = corpusResponses as Record<string, CorpusResponse>;

// The response synthesizer's pipeline, surfaced as honest progress steps:
// hybrid retrieval → confidence/mode selection → cited synthesis (the long tail).
const RESPONSE_STEPS = [
  "Searching the corpus",
  "Weighing the evidence",
  "Composing a cited answer",
] as const;

/**
 * Interpretive and not-in-corpus surfaces stay curated; evidential and partial
 * go live.
 *
 * The live `/agent/respond` pipeline never emits "interpretive" (it isn't
 * auto-selected), and because semantic search always returns *something*, a
 * genuine "not in corpus" can't be reproduced live without synthesizing an
 * answer from loosely-related docs — the exact fabrication the product
 * refuses. So those two remain curated showcases; the answerable modes are
 * served live for real, corpus-grounded answers with working document links.
 */
function isCuratedMode(mode: ResponseMode): boolean {
  return mode === "interpretive" || mode === "not_in_corpus";
}

export const Route = createFileRoute("/response/$queryId")({
  component: ResponsePage,
});

function ResponsePage() {
  const { queryId } = Route.useParams();
  const curated = RESPONSES[queryId];

  if (!curated) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-16 text-center">
        <p className="text-caps uppercase tracking-caps text-ink-tertiary">Not found</p>
        <h1 className="mt-3 font-serif text-display text-ink-primary">No response for “{queryId}”</h1>
        <p className="mx-auto mt-3 max-w-md font-serif text-lede italic text-ink-secondary">
          That query isn’t in the curated set yet.
        </p>
        <Link
          to="/"
          search={{ mode: "query" }}
          className="mt-6 inline-block text-meta text-ink-info hover:text-ink-primary"
        >
          ← Back to the query page
        </Link>
      </div>
    );
  }

  if (isCuratedMode(curated.mode)) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
        <ResponseSurface response={curated} />
      </div>
    );
  }

  return <LiveResponse curated={curated} />;
}

/** Evidential / partial featured surface — synthesized live from the corpus,
 *  with the curated fixture as a graceful fallback if the call fails. */
function LiveResponse({ curated }: { curated: CorpusResponse }) {
  const { data, isLoading, isError } = useResponseSurface(curated.query_text);

  if (isLoading) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-10 lg:px-10">
        <AgentLoadingState
          query={curated.query_text}
          heading="Synthesizing a verified answer from the corpus…"
          steps={RESPONSE_STEPS}
          advanceMs={3200}
        />
      </div>
    );
  }

  // Use the live answer only when it actually answered. If the call errored,
  // or returned not-in-corpus (e.g. a deployment whose backend isn't wired to
  // the populated corpus), fall back to the curated fixture so the featured
  // surface is never a broken non-answer.
  const answered = !isError && data && data.mode !== "not_in_corpus";
  const body = answered ? data : curated;
  // Always present the featured question and its curated follow-up suggestion,
  // even though the body (prose + citations) comes live.
  const response: CorpusResponse = {
    ...body,
    query_text: curated.query_text,
    follow_up_placeholder: curated.follow_up_placeholder,
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 lg:px-10">
      <ResponseSurface response={response} />
    </div>
  );
}
